import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ClipboardCheck, Clock, AlertCircle, CheckCircle2, FileText, Search } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  todo: "bg-slate-500/10 text-slate-600 border-slate-500/30",
  in_progress: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  done: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
};

const CATEGORIES = ["general", "paperwork", "it_setup", "orientation", "training", "compliance"];

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Onboarding() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const empty = { employee_id: "", title: "", description: "", status: "todo", category: "general", assignee: "", due_date: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [tRes, sRes, eRes] = await Promise.all([
        axios.get(`${API}/onboarding/tasks`),
        axios.get(`${API}/onboarding/stats`),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
      ]);
      setTasks(tRes.data || []);
      setStats(sRes.data || {});
      setEmployees(eRes.data || []);
    } catch (e) { toast.error("Failed to load onboarding data"); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      const payload = { ...form };
      if (!payload.employee_id) delete payload.employee_id;
      if (!payload.due_date) delete payload.due_date;
      if (editing) await axios.put(`${API}/onboarding/tasks/${editing.id}`, payload);
      else await axios.post(`${API}/onboarding/tasks`, payload);
      toast.success(editing ? "Task updated" : "Task created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this onboarding task?")) return;
    try { await axios.delete(`${API}/onboarding/tasks/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const cycleStatus = async (t) => {
    const next = t.status === "todo" ? "in_progress" : t.status === "in_progress" ? "done" : "todo";
    try { await axios.put(`${API}/onboarding/tasks/${t.id}`, { status: next }); load(); }
    catch { toast.error("Status update failed"); }
  };

  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? e.name : (id ? "Unknown" : "—");
  };

  const filtered = tasks.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (search && !`${t.title} ${t.description || ""} ${t.assignee || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Employee Onboarding</h1>
          <p className="text-sm text-muted-foreground mt-1">Track new hire checklists, IT setup, paperwork, and orientation tasks.</p>
        </div>
        <Button onClick={openAdd} data-testid="add-onboarding-task-btn"><Plus className="h-4 w-4 mr-2" />New Task</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={ClipboardCheck} label="Total" value={stats.total ?? 0} tint="bg-primary/10 text-primary" />
        <StatCard icon={Clock} label="To Do" value={stats.todo ?? 0} tint="bg-slate-500/10 text-slate-600" />
        <StatCard icon={Clock} label="In Progress" value={stats.in_progress ?? 0} tint="bg-blue-500/10 text-blue-600" />
        <StatCard icon={CheckCircle2} label="Done" value={stats.done ?? 0} tint="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={AlertCircle} label="Overdue" value={stats.overdue ?? 0} tint="bg-red-500/10 text-red-600" />
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="onboarding-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="onboarding-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="done">Done</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No onboarding tasks yet. Click "New Task" to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`onboarding-row-${t.id}`}>
                    <TableCell>
                      <div className="font-medium">{t.title}</div>
                      {t.description && <div className="text-xs text-muted-foreground line-clamp-1">{t.description}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{empName(t.employee_id)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.category}</Badge></TableCell>
                    <TableCell className="text-sm">{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`cursor-pointer ${STATUS_COLOR[t.status]}`} onClick={() => cycleStatus(t)} data-testid={`status-${t.id}`}>
                        {t.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(t.id)} data-testid={`delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Task" : "New Onboarding Task"}</DialogTitle>
            <DialogDescription>Track an action item for a new hire's onboarding journey.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="onboarding-title-input" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Employee</Label>
                <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Assignee</Label><Input value={form.assignee || ""} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Manager / HR contact" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-onboarding-task-btn">{editing ? "Save Changes" : "Create Task"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
